import type { ChannelReads } from '@hvf/contracts'
import { createMutation, createQuery, useQueryClient } from '@tanstack/solid-query'
import { useService } from 'solid-services'
import { userId } from '../auth'
import { useChannels } from '../channels/channels'
import { useMessageMeta } from '../messageMeta'
import { handleGetChannelReads, upsertChannelRead } from './resources'

export function useChannelsReads() {
  const queryClient = useQueryClient()
  const { messageMetaResource } = useService(useMessageMeta)()
  const { mutateChannels } = useService(useChannels)()

  // 1. Fetch channel reads using Solid Query
  const channelReadsQuery = createQuery(() => ({
    queryKey: ['channelReads', userId()],
    queryFn: () => handleGetChannelReads(userId()!),
    enabled: !!userId(),
  }))

  const channelReadDictionary = () => {
    const reads = channelReadsQuery.data
    if (reads) {
      return reads.reduce(
        (acc, read) => {
          acc[read.channel_id] = read
          return acc
        },
        {} as Record<string, ChannelReads>,
      )
    }
    return {}
  }

  // 2. Setup the explicit mutation
  const upsertReadMutation = createMutation(() => ({
    mutationFn: upsertChannelRead,
    // Add data and variables arguments here
    onSuccess: (data, variables) => {
      // Refresh the user's reads
      queryClient.invalidateQueries({ queryKey: ['channelReads', userId()] })

      // NEW: Tell the UI to refresh the community subscriber count for this specific channel
      if (variables.channelRead?.channel_id) {
        queryClient.invalidateQueries({
          queryKey: ['subscribedCount', variables.channelRead.channel_id]
        })
      }
    },
  }))

  const setChannelRead = (channelRead: Partial<ChannelReads>) => {
    const uid = userId()
    if (!uid) return
    upsertReadMutation.mutate({ userId: uid, channelRead })
  }

  const setChannelReadSubscription = (is_subscribed: boolean, channelId: string) => {
    const dict = channelReadDictionary()
    const c = dict[channelId] || { channel_id: channelId }

    setChannelRead({
      ...c,
      is_subscribed,
    })
  }

  const toggleSubscribed = async (channelId?: string) => {
    if (!channelId) return

    let nextSubscribedState = true
    const channelReadDict = channelReadDictionary()
    const existing = channelReadDict[channelId]

    if (existing) {
      nextSubscribedState = !existing.is_subscribed // Toggle the state
      setChannelReadSubscription(nextSubscribedState, channelId)
    } else {
      // create new channel read
      nextSubscribedState = true
      const meta = messageMetaResource()?.find((meta) => meta.channel_id === channelId)

      setChannelRead({
        channel_id: channelId,
        last_read_message_id: meta?.last_message_id,
        last_read_at: meta?.last_message_at,
        is_subscribed: nextSubscribedState,
      })
    }

    // Optimistically update the main channel list UI
    mutateChannels((prevChannels) => {
      if (!prevChannels) return prevChannels

      const channelIndex = prevChannels.findIndex((c) => c.id === channelId)
      if (channelIndex === -1) return prevChannels

      const newChannels = [...prevChannels]
      newChannels[channelIndex] = {
        ...newChannels[channelIndex],
        is_subscribed: nextSubscribedState,
      }

      return newChannels
    })
  }

  return {
    channelReadsQuery,
    channelReadDictionary,
    upsertReadMutation,
    setChannelRead,
    setChannelReadSubscription,
    toggleSubscribed,
  }
}
